import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

// AsciiTechSection — 3D ASCII animation (Three.js AsciiEffect) + heading copy
// Drop-in section to place under TabbedWorkflowSection in LandingPage.jsx

const BRAND_COLOR_DEFAULT = '#1e40af';

const densityToFontPx = {
  low: 14,
  med: 12,
  high: 10,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function isWebGLAvailable() {
  if (typeof window === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch (e) {
    return false;
  }
}

// Utility: choose ASCII character by luminance (kept for potential custom effects)
export function luminanceToChar(luma, density = 'med') {
  const sets = {
    low: " .:-=+*#%@",
    med: " .:-=+*#%@",
    high: " .'`^\",:;Il!i><~+_-?][}{1)(|\\/tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$",
  };
  const chars = sets[density] || sets.med;
  const idx = Math.round(clamp(luma, 0, 1) * (chars.length - 1));
  return chars[idx];
}

const AsciiTechSection = ({
  className = '',
  headingOverride,
  density = 'med',
  color = BRAND_COLOR_DEFAULT,
}) => {
  const wrapperRef = useRef(null);
  const asciiHostRef = useRef(null);
  const effectRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const meshRef = useRef(null);
  const rafRef = useRef(0);
  const visibleRef = useRef(true);
  const lastFrameTimeRef = useRef(0);
  const reducedMotionRef = useRef(false);
  const [fallback, setFallback] = useState(false);

  const headingText = headingOverride || "AI-Native Contract Generation.";
  const subheadingText = "Intelligent contract creation powered by AI that understands your business. Generate legally-binding contracts in seconds with industry-specific templates tailored for small businesses and freelancers.";

  const fontPx = densityToFontPx[density] || densityToFontPx.med;

  // Mouse parallax state (no re-renders)
  const parallaxRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0 });

  // Observe visibility to pause when offscreen
  useEffect(() => {
    if (!wrapperRef.current || typeof window === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibleRef.current = entry.isIntersecting;
        }
      },
      { threshold: 0.05 }
    );
    io.observe(wrapperRef.current);
    return () => io.disconnect();
  }, []);

  // Reduced motion
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => (reducedMotionRef.current = mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update);
    };
  }, []);

  // Core setup (lazy-load AsciiEffect)
  useEffect(() => {
    let disposed = false;
    if (typeof window === 'undefined') return;
    if (!asciiHostRef.current) return;
    if (!isWebGLAvailable()) {
      setFallback(true);
      return;
    }

    const init = async () => {
      try {
        const mod = await import('three/examples/jsm/effects/AsciiEffect.js');
        const AsciiEffect = mod.AsciiEffect || mod.default;

        if (disposed) return;

        const host = asciiHostRef.current;
        // Clear any previous content (hot reload safety)
        host.innerHTML = '';

        const scene = new THREE.Scene();
        scene.background = null;
        sceneRef.current = scene;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        rendererRef.current = renderer;

        const camera = new THREE.PerspectiveCamera(45, 4 / 3, 0.1, 100);
        camera.position.set(0, 0, 4);
        cameraRef.current = camera;

        // Lights for shading contrast
        const ambient = new THREE.AmbientLight(0xffffff, 0.6);
        const dir = new THREE.DirectionalLight(0xffffff, 0.9);
        dir.position.set(3, 4, 5);
        scene.add(ambient);
        scene.add(dir);

        // Geometry & material (TorusKnot)
        const geo = new THREE.TorusKnotGeometry(0.9, 0.3, 220, 32);
        const mat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.3, metalness: 0.1, flatShading: true });
        const mesh = new THREE.Mesh(geo, mat);
        meshRef.current = mesh;
        scene.add(mesh);

        // AsciiEffect
        const asciiChars = density === 'high' ? " .'`^\",:;Il!i><~+_-?][}{1)(|\\/tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$" : " .:-=+*#%@";
        const effect = new AsciiEffect(renderer, asciiChars, { invert: true });
        effectRef.current = effect;
        effect.domElement.style.color = color;
        effect.domElement.style.background = 'transparent';
        effect.domElement.style.pointerEvents = 'none';
        effect.domElement.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
        effect.domElement.style.fontSize = fontPx + 'px';
        effect.domElement.style.lineHeight = '1em';
        effect.domElement.style.position = 'absolute';
        effect.domElement.style.top = '50%';
        effect.domElement.style.left = '50%';
        effect.domElement.style.transform = 'translate(-50%, -50%)';
        effect.domElement.setAttribute('aria-hidden', 'true');

        host.appendChild(effect.domElement);

        // Resize handling: true "cover" to fill host while preserving 4:3
        const setSize = () => {
          const containerWidth = Math.max(320, host.clientWidth);
          const containerHeight = Math.max(240, host.clientHeight);
          const desiredAspect = 4 / 3;

          let renderWidth = containerWidth;
          let renderHeight = Math.round(renderWidth / desiredAspect);
          if (renderHeight < containerHeight) {
            renderHeight = containerHeight;
            renderWidth = Math.round(renderHeight * desiredAspect);
          }

          camera.aspect = renderWidth / renderHeight;
          camera.updateProjectionMatrix();
          effect.setSize(renderWidth, renderHeight);
        };
        setSize();

        const ro = new ResizeObserver(setSize);
        ro.observe(host);

        // Mouse parallax
        const onMouseMove = (e) => {
          const rect = host.getBoundingClientRect();
          const nx = (e.clientX - rect.left) / rect.width;
          const ny = (e.clientY - rect.top) / rect.height;
          parallaxRef.current.targetX = (nx - 0.5) * 0.6; // clamp later
          parallaxRef.current.targetY = (ny - 0.5) * 0.6;
        };
        window.addEventListener('mousemove', onMouseMove);

        // Animation loop
        const animate = (timeMs) => {
          const now = timeMs || performance.now();
          const docHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
          const targetInterval = (reducedMotionRef.current || docHidden || !visibleRef.current) ? 1000 / 30 : 1000 / 60;
          const since = now - (lastFrameTimeRef.current || 0);
          if (since < targetInterval) {
            rafRef.current = requestAnimationFrame(animate);
            return;
          }
          lastFrameTimeRef.current = now;

          // Parallax easing
          parallaxRef.current.x += (parallaxRef.current.targetX - parallaxRef.current.x) * 0.08;
          parallaxRef.current.y += (parallaxRef.current.targetY - parallaxRef.current.y) * 0.08;
          const px = clamp(parallaxRef.current.x, -0.3, 0.3);
          const py = clamp(parallaxRef.current.y, -0.3, 0.3);

          // Rotate mesh + subtle wobble
          const t = now * 0.001;
          if (mesh) {
            mesh.rotation.x = t * 0.8 + py;  // Increased from 0.5 to 0.8
            mesh.rotation.y = t * 1.2 + px;  // Increased from 0.7 to 1.2
            const s = 1 + 0.05 * Math.sin(t * 1.2);  // Increased from 0.8 to 1.2
            mesh.scale.set(s, s, s);
          }

          // Faster orbit camera
          const r = 4;
          camera.position.x = Math.cos(t * 0.35) * r + px;  // Increased from 0.2 to 0.35
          camera.position.y = Math.sin(t * 0.25) * 0.4 + py;  // Increased from 0.15 to 0.25
          camera.position.z = 3.2 + 0.2 * Math.sin(t * 0.4);  // Increased from 0.25 to 0.4
          camera.lookAt(0, 0, 0);

          effect.render(scene, camera);
          rafRef.current = requestAnimationFrame(animate);
        };

        // If reduced motion, render a single frame and skip loop
        if (reducedMotionRef.current) {
          if (mesh) {
            mesh.rotation.set(0.3, 0.6, 0);
            mesh.scale.set(1.03, 1.03, 1.03);
          }
          effect.render(scene, camera);
        } else {
          rafRef.current = requestAnimationFrame(animate);
        }

        // Cleanup
        const cleanup = () => {
          cancelAnimationFrame(rafRef.current);
          window.removeEventListener('mousemove', onMouseMove);
          ro.disconnect();
          if (effect && effect.domElement && effect.domElement.parentNode === host) {
            host.removeChild(effect.domElement);
          }
          if (mesh) {
            mesh.geometry && mesh.geometry.dispose();
            mesh.material && mesh.material.dispose();
            scene.remove(mesh);
          }
          renderer.dispose();
        };
        return cleanup;
      } catch (err) {
        console.warn('AsciiEffect failed to load, falling back to donut:', err);
        setFallback(true);
        return () => {};
      }
    };

    let cleanupFn = () => {};
    init().then((cleanup) => {
      if (cleanup) cleanupFn = cleanup;
    });

    return () => {
      disposed = true;
      cleanupFn();
    };
  }, [density, color, fontPx]);

  return (
    <>
      {/* Section Divider - Rail-style horizontal lines */}
      <div className="relative w-full">
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
        {/* Gap between double rails */}
        <div 
          className="w-full"
          style={{
            height: '8px',
            background: 'transparent'
          }}
        />
        {/* Double rail effect - second line */}
        <div 
          className="w-full"
          style={{
            borderTop: '1.5px solid rgba(30, 64, 175, 0.25)'
          }}
        />
      </div>

      <section ref={wrapperRef} className={`relative bg-white ${className}`}>
        {/* Simple two-column split layout */}
        <div className="flex flex-col md:flex-row min-h-[700px]">
          
          {/* Left Half: ASCII animation */}
          <div className="w-full md:w-1/2 h-[400px] md:h-[700px] relative">
            <div
              ref={asciiHostRef}
              className="w-full h-full"
              style={{
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {fallback && (
                <DonutFallback color={color} density={density} />
              )}
            </div>
            
            {/* Vertical divider at the right edge of ASCII container */}
            <div className="hidden md:block absolute inset-y-0 right-0 pointer-events-none">
              <div className="h-full" style={{ borderRight: '1.5px solid rgba(30, 64, 175, 0.25)' }} />
            </div>
          </div>

          {/* Right Half: Content - Simple heading and subheading only */}
          <div className="w-full md:w-1/2 flex items-center px-6 md:px-12 lg:px-16 py-12 md:py-16">
            <div className="max-w-xl">
              {/* Section Label */}
              <div 
                className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6" 
                style={{
                  backgroundColor: 'rgba(30, 64, 175, 0.1)', 
                  color: '#1e40af',
                  fontWeight: 500,
                  letterSpacing: '-0.01em'
                }}
              >
                AI-Powered Intelligence
              </div>

              {/* Main Heading */}
              <h3 
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" 
                style={{ 
                  color: '#333333',
                  fontWeight: 600,
                  letterSpacing: '-0.03em',
                  lineHeight: '1.1',
                  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif"
                }}
              >
                {headingText}
              </h3>

              {/* Subheading */}
              <p 
                className="text-xl" 
                style={{ 
                  color: '#636363',
                  fontWeight: 400,
                  letterSpacing: '-0.01em',
                  lineHeight: '1.4',
                  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif"
                }}
              >
                {subheadingText}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// Ultra-light ASCII donut fallback (no WebGL)
const DonutFallback = ({ color = BRAND_COLOR_DEFAULT, density = 'med' }) => {
  const preRef = useRef(null);
  const rafRef = useRef(0);
  const runningRef = useRef(true);
  const reducedMotionRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => (reducedMotionRef.current = mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update);
    };
  }, []);

  useEffect(() => {
    if (!preRef.current) return;
    const el = preRef.current;
    let A = 0, B = 0;
    const render = () => {
      const width = 60;
      const height = 22;
      const b = new Array(width * height).fill(' ');
      const z = new Array(width * height).fill(0);
      for (let j = 0; j < 6.28; j += 0.07) {
        for (let i = 0; i < 6.28; i += 0.02) {
          const cA = Math.cos(A), sA = Math.sin(A);
          const cB = Math.cos(B), sB = Math.sin(B);
          const c = Math.sin(i), d = Math.cos(j);
          const e = Math.sin(j), f = Math.cos(i);
          const h = f + 2;
          const D = 1 / (c * h * sA + e * cA + 5);
          const t = c * h * cA - e * sA;
          const x = Math.floor(width / 2 + (width * 0.35) * D * (d * h * cB - t * sB));
          const y = Math.floor(height / 2 + (height * 0.7) * D * (d * h * sB + t * cB));
          const o = x + width * y;
          const N = 0 | (8 * ((e * sA - c * h * cA) * cB - c * h * sA - e * cA - d * f * sB));
          if (y >= 0 && y < height && x >= 0 && x < width && D > z[o]) {
            z[o] = D;
            b[o] = luminanceToChar((N + 8) / 16, density);
          }
        }
      }
      el.textContent = b.join('').match(/.{1,60}/g).join('\n');
      A += 0.07;  // Increased from 0.04 to 0.07
      B += 0.035;  // Increased from 0.02 to 0.035
    };
    const step = () => {
      render();
      if (!runningRef.current) return;
      rafRef.current = requestAnimationFrame(step);
    };
    if (reducedMotionRef.current) {
      render();
    } else {
      rafRef.current = requestAnimationFrame(step);
    }
    return () => {
      runningRef.current = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [density]);

  return (
    <pre
      ref={preRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        margin: 0,
        color,
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        fontSize: (densityToFontPx[density] || 12) + 'px',
        lineHeight: '1em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        userSelect: 'none',
        pointerEvents: 'none',
      }}
    />
  );
};

export default AsciiTechSection;


